import {
  EVENTS_APP_ID,
  EVENT_FILTER_TYPE,
  ExperimentNames,
  FEATURED_EVENT_DISPLAY_STRATEGY,
  LIST_DISPLAY_STRATEGY,
  WIDGET_TYPE,
  isRtlLanguage,
  sortEvents,
} from '@wix/wix-events-commons-statics'
import {ControllerParams, CreateControllerFn, ExperimentsBag} from '@wix/yoshi-flow-editor'
import {PreloadedState, Store, bindActionCreators} from 'redux'
import {setBaseEnvironment, setBaseEnvironmentOld, setFormFactor} from '../../commons/actions/environment'
import {watchInstance} from '../../commons/actions/instance'
import {createUouBiMiddlewareWithBiParams} from '../../commons/bi/bi'
import {ALL_CATEGORIES_FILTER, EMPTY_CATEGORY_FILTER} from '../../commons/constants/settings'
import {UserRole} from '../../commons/enums'
import {createErrorLoggerMiddleware} from '../../commons/middlewares/error-logger'
import {getInitialCategoryId} from '../../commons/selectors/categories'
import {isResponsiveEditor} from '../../commons/selectors/environment'
import {isEventsInMembersInstalled} from '../../commons/selectors/installed-apps'
import {decodeInstance} from '../../commons/selectors/instance'
import {getInstalledAppsState} from '../../commons/services/installed-apps'
import {getMultilingualInitialState} from '../../commons/services/multilingual'
import {getQueryParams} from '../../commons/services/navigation'
import {createReduxStore} from '../../commons/utils/store'
import {getLanguage, getPageInfo, isEditor, isEditor3} from '../../commons/utils/wix-code-api'
import settingsParams from './settingsParams'
import {CommonWidgetDataParams} from './shared-api'
import {getDefaultNumbersAndBooleans} from './stylesParams'
import {
  addCalendarMonth,
  addCalendarMonthOld,
  closeMonthlyCalendarEvent,
  closeMonthlyCalendarPopup,
  loadCalendar,
  openMonthlyCalendarEvent,
  openMonthlyCalendarPopup,
  resetCalendar,
  resetCalendarOld,
  subCalendarMonth,
  subCalendarMonthOld,
} from './Widget/actions/calendar-layout'
import {selectCategory} from './Widget/actions/categories'
import {updateStyleParams, updateStyles} from './Widget/actions/component'
import {shareEvent} from './Widget/actions/event'
import {loadEventsPage} from './Widget/actions/events'
import {closeListLayoutItems, openListLayoutItem, widgetLoaded} from './Widget/actions/layout'
import {
  addLoginListenerOld,
  loadMembersForEvents,
  memberLoggedIn,
  openMembersModal,
  openMembersModalOld,
} from './Widget/actions/members'
import {navigateToPage, navigateToPageOld, onLinkNavigatedToPage} from './Widget/actions/navigate-to-page'
import {SettingsUpdateAction, updateSettings, updateSettingsOld} from './Widget/actions/sdk'
import * as eventsUou from './Widget/bi/uou-bi-events-map'
import {AppProps} from './Widget/components/app/interfaces'
import {datesMiddleware} from './Widget/middlewares/date'
import reducers from './Widget/reducers'
import {
  getInitialCategoryFilter,
  getInitialFetchCategoryId,
  getSelectedCategoryFilterIds,
  isCategoryMenuMode,
} from './Widget/selectors/categories'
import {getDynamicCategoryId} from './Widget/selectors/velo-overrides'
import {renderSEOTags} from './Widget/services/seo'
import {Actions, State, StoreExtraArgs} from './Widget/types/state'
import {userEventsLogger} from './Widget/user-events-logger'
import {Api} from './Widget/utils/api'

const createWidgetController: CreateControllerFn = async (controllerParams: ControllerParams) => {
  const experiments = controllerParams.flowAPI.experiments.all()
  const serverApi = new Api(controllerParams)
  const store = await createStore({controller: controllerParams, serverApi, experiments})

  return {
    pageReady: async () => {
      return pageReady({controllerParams, store, serverApi})
    },
    exports: () => ({
      set categoryId(id: string) {
        store.dispatch(selectCategory(id))
      },
      get categoryId() {
        return getDynamicCategoryId(store.getState(), controllerParams.flowAPI)
      },
    }),
    updateAppSettings: async (_$w, action) => {
      if (store) {
        setTimeout(() => {
          controllerParams.flowAPI.experiments.enabled(ExperimentNames.UseEventsViewerReduxToolkit)
            ? store.dispatch(updateSettings(action as SettingsUpdateAction))
            : store.dispatch(updateSettingsOld(action as SettingsUpdateAction))
        }, 100)
      }
    },
    updateConfig: (_$w, newConfig) => {
      store.dispatch(
        updateStyles({
          numbers: newConfig.style.styleParams.numbers,
          booleans: newConfig.style.styleParams.booleans,
        }),
      )
    },
  }
}

interface PageReadyParams {
  controllerParams: ControllerParams
  store: Store<State>
  serverApi: Api
}

const pageReady = async ({store, controllerParams}: PageReadyParams) => {
  const {controllerConfig: controller} = controllerParams
  const language = getLanguage(controller.wixCodeApi)
  const featuredWidget = controller.config.style.styleParams.numbers.widgetType === WIDGET_TYPE.FEATURED

  const [pageInfo, pageUrl] = await Promise.all([
    getPageInfo(controller.wixCodeApi),
    controller.wixCodeApi.site.getSectionUrl({
      sectionId: 'events',
      appDefinitionId: EVENTS_APP_ID,
    }),
  ])

  const state = store.getState()
  const props: Partial<AppProps> = {
    pageUrl,
    baseUrl: controller.wixCodeApi.location.baseUrl,
    queryParams: getQueryParams(controller.wixCodeApi),
    pageInfo,
    state,
    actions: exportedActions({store}),
    isRTL: isRtlLanguage(language),
    // @ts-expect-error
    fitToContentHeight: true,
    featuredWidget,
  }

  if (controllerParams.flowAPI.experiments.enabled(ExperimentNames.WidgetSEOTags)) {
    await renderSEOTags({wixCodeApi: controller.wixCodeApi, state, pageUrl: pageUrl.url})
  }

  controller.setProps(props)
  subscribeToStateChanges(controllerParams, store)
}

interface CreateStoreParams {
  controller: ControllerParams
  serverApi: Api
  experiments: ExperimentsBag
}

const createStore = async ({controller, serverApi, experiments}: CreateStoreParams) => {
  const {
    controllerConfig: {wixCodeApi, platformAPIs, appParams, compId, config},
    flowAPI,
  } = controller

  let initialData: Awaited<ReturnType<typeof getInitialData>>
  const warmupDataKey = `widget${compId}`

  if (flowAPI.experiments.enabled(ExperimentNames.UseWarmupState) && flowAPI.environment.isSSR) {
    initialData = await getInitialData({serverApi, controller, experiments})
    wixCodeApi.window.warmupData.set(warmupDataKey, initialData)
  } else {
    initialData =
      wixCodeApi.window.warmupData.get(warmupDataKey) ?? (await getInitialData({serverApi, controller, experiments}))
  }

  const store = createReduxStore<State, StoreExtraArgs>({
    reducers,
    initialData,
    extraArguments: {
      serverApi,
      wixCodeApi,
      compId,
      flowAPI,
    },
    middleware: [
      createUouBiMiddlewareWithBiParams({wixCodeApi, flowAPI, platformAPIs, appParams, compId}, eventsUou),
      userEventsLogger({wixCodeApi}),
      datesMiddleware,
      createErrorLoggerMiddleware({errorMonitor: flowAPI.panoramaClient.errorMonitor}),
    ],
  })

  store.dispatch(updateStyleParams(config.style.styleParams))

  flowAPI.experiments.enabled(ExperimentNames.UseEventsViewerReduxToolkit)
    ? wixCodeApi.user.onLogin(async user => {
        if (user.loggedIn) {
          store.dispatch(memberLoggedIn())
        }
      })
    : store.dispatch(addLoginListenerOld())

  flowAPI.experiments.enabled(ExperimentNames.UseEventsViewerReduxToolkit)
    ? await store.dispatch(setBaseEnvironment())
    : await store.dispatch(setBaseEnvironmentOld())

  const {listShowMembers = true, showMembers = true} = config.style.styleParams.booleans
  const membersVisible =
    config.style.styleParams.numbers.widgetType === WIDGET_TYPE.SINGLE ? showMembers : listShowMembers
  const eventsInMembersInstalled = isEventsInMembersInstalled(store.getState())

  if (flowAPI.experiments.enabled(ExperimentNames.FixLoadMembers) && membersVisible && eventsInMembersInstalled) {
    store.dispatch(loadMembersForEvents())

    serverApi.onLogin(() => {
      store.dispatch(loadMembersForEvents())
    })
  }

  watchInstance(controller.controllerConfig, store.dispatch)

  return store
}

const subscribeToStateChanges = (controllerParams: ControllerParams, store: Store<State>) => {
  const onStateChange = () => {
    const state = store.getState()

    controllerParams.controllerConfig.setProps({state})
  }

  store.subscribe(onStateChange)
}

interface GetInitialDataParams {
  serverApi: Api
  experiments: ExperimentsBag
  controller: ControllerParams
}

const getInitialData = async ({serverApi, controller}: GetInitialDataParams): Promise<PreloadedState<State>> => {
  const {compId, language, locale, tz, viewMode} = serverApi
  const {
    flowAPI,
    controllerConfig: {
      wixCodeApi,
      appParams: {instance},
      config: controllerConfig,
    },
  } = controller

  const updatedDisplayListEventsPanelEnabled = flowAPI.experiments.enabled(
    ExperimentNames.UpdatedDisplayListEventsPanel,
  )
  const newClassicEvents = flowAPI.experiments.enabled(ExperimentNames.NewClassicEvents)

  const filterType = flowAPI.settings.get(settingsParams.filterType) as number
  const sortOrder = flowAPI.settings.get(settingsParams.sortOrder) as number
  const categoryId = flowAPI.settings.get(settingsParams.categoryId) as string
  const limit = flowAPI.settings.get(settingsParams.eventsPerPage) as number

  const listDisplayStrategy = flowAPI.settings.get(settingsParams.listDisplayStrategy) as LIST_DISPLAY_STRATEGY
  const listStatusFilter = flowAPI.settings.get(settingsParams.listStatusFilter) as number

  const featuredCategoryId = flowAPI.settings.get(settingsParams.featuredCategoryId) as string
  const featuredEventId = flowAPI.settings.get(settingsParams.featuredEventId) as string
  const featuredDisplayStrategy = flowAPI.settings.get(
    settingsParams.featuredEventDisplayStrategy,
  ) as FEATURED_EVENT_DISPLAY_STRATEGY

  const installedApps = await getInstalledAppsState(wixCodeApi)
  const {eventsInMembersInstalled, paidPlans: paidPlansInstalled} = installedApps

  const decodedInstance = decodeInstance(instance)
  const responsive = isResponsiveEditor(controllerConfig)
  const editor = isEditor(wixCodeApi)
  const editor3 = isEditor3(wixCodeApi)
  const showcase = decodedInstance.siteIsTemplate || editor

  const {listShowMembers = true, showMembers = true} = controllerConfig.style.styleParams.booleans
  const {widgetType, listLayout, recurringFilter} = controllerConfig.style.styleParams.numbers
  const featuredWidget = widgetType === WIDGET_TYPE.FEATURED
  const singleWidget = widgetType === WIDGET_TYPE.SINGLE

  const commonArgs: CommonWidgetDataParams = {
    members: eventsInMembersInstalled && (singleWidget ? showMembers : listShowMembers),
    paidPlans: paidPlansInstalled,
    responsive,
    language,
    locale,
    tz,
    viewMode,
    newClassicEvents,
    editor3,
  }

  const statusFilter = updatedDisplayListEventsPanelEnabled
    ? listDisplayStrategy === LIST_DISPLAY_STRATEGY.CUSTOM
      ? EVENT_FILTER_TYPE.MANUAL
      : listStatusFilter
    : filterType

  const categoryMenuMode = isCategoryMenuMode(flowAPI.settings.get)

  const getCategoryIdFilter = () => {
    if (categoryMenuMode) {
      return getInitialCategoryFilter(flowAPI.settings.get)
    }

    if (listDisplayStrategy === LIST_DISPLAY_STRATEGY.MULTIPLE_CATEGORIES) {
      const selectedIds = getSelectedCategoryFilterIds(flowAPI.settings.get)

      if (!selectedIds.length) {
        return [EMPTY_CATEGORY_FILTER]
      }

      return selectedIds
    }

    if (
      (updatedDisplayListEventsPanelEnabled && listDisplayStrategy !== LIST_DISPLAY_STRATEGY.CATEGORY) ||
      categoryId === EMPTY_CATEGORY_FILTER
    ) {
      return undefined
    }

    return categoryId
  }

  const categoryIdFilter: string | string[] = getCategoryIdFilter()
  const fetchCategoryId = getInitialFetchCategoryId(flowAPI.settings.get)

  const initialCategoryId = categoryMenuMode ? getInitialCategoryId(flowAPI.settings.get) : null
  const initialVeloCategoryId = initialCategoryId === ALL_CATEGORIES_FILTER ? null : initialCategoryId

  const {component, siteSettings, demoEvents, hasMoreEvents, dates, categories} = featuredWidget
    ? await serverApi.getFeaturedWidgetData({
        ...commonArgs,
        eventId: featuredEventId,
        categoryId: featuredCategoryId,
        manuallySelected: featuredDisplayStrategy === FEATURED_EVENT_DISPLAY_STRATEGY.CUSTOM,
      })
    : await serverApi.getWidgetData({
        showcase,
        widgetType,
        listLayout,
        recurringFilter,
        categoryId: categoryIdFilter,
        noCategoryIdFallback:
          categoryIdFilter === EMPTY_CATEGORY_FILTER || categoryIdFilter?.includes?.(EMPTY_CATEGORY_FILTER),
        compId,
        filterType: statusFilter,
        sortOrder: filterType === EVENT_FILTER_TYPE.MANUAL ? sortOrder : undefined,
        limit: editor ? 20 : limit,
        fetchCategoryId,
        fetchAllCategories: editor,
        ...commonArgs,
      })

  const settings = getDefaultNumbersAndBooleans({
    isMobile: flowAPI.environment.isMobile,
    isRTL: flowAPI.environment.isRTL,
  })

  const events = component.featuredEvent ? [component.featuredEvent] : component.events ?? []

  return {
    events: {
      events: filterType === EVENT_FILTER_TYPE.MANUAL ? events : sortEvents(events),
      hasMore: hasMoreEvents,
      moreLoading: false,
      filterType,
      latestRequestId: '',
    },
    siteSettings,
    demoEvents: {
      ...demoEvents,
      [WIDGET_TYPE.FEATURED]: demoEvents?.[WIDGET_TYPE.LIST],
    },
    multilingual: getMultilingualInitialState(wixCodeApi),
    component: {
      id: compId,
      settings,
    },
    memberLoggedIn: wixCodeApi.user.currentUser.loggedIn,
    owner: wixCodeApi.user.currentUser.role === UserRole.ADMIN,
    instance: {
      instance,
      ...decodedInstance,
    },
    calendarLayout: {
      ...(dates.calendar ?? {}),
      loadedPages: dates.calendar?.referenceDate ? [dates.calendar.referenceDate] : [],
    },
    categories: {
      categories: categories ?? [],
    },
    dynamicVeloOverrides: {
      categoryId: initialVeloCategoryId,
    },
    dates,
    installedApps,
  } as PreloadedState<State>
}

const exportedActions = ({store}) => {
  const dispatchActions = {
    loadCalendar,
    addCalendarMonth,
    addCalendarMonthOld,
    subCalendarMonth,
    subCalendarMonthOld,
    resetCalendar,
    resetCalendarOld,
    navigateToPageOld,
    navigateToPage,
    onLinkNavigatedToPage,
    updateStyles,
    updateSettingsOld,
    updateSettings,
    setBaseEnvironmentOld,
    setBaseEnvironment,
    openListLayoutItem,
    closeListLayoutItems,
    openMonthlyCalendarPopup,
    closeMonthlyCalendarPopup,
    openMonthlyCalendarEvent,
    closeMonthlyCalendarEvent,
    widgetLoaded,
    openMembersModalOld,
    openMembersModal,
    shareEvent,
    setFormFactor,
    loadEventsPage,
    selectCategory,
  }

  const actions: Actions = {
    ...bindActionCreators(dispatchActions, store.dispatch),
  }

  return actions
}

export default createWidgetController
